import { type FC, useId, useMemo, useState } from "react"
import TextAreaAutoSize from "react-textarea-autosize"
import styled, { css } from "styled-components"

import LexicalEditor, { type LexicalEditorType } from "@forento/shared-client/components/LexicalEditor"
import { exportEditorState, loadEditorState } from "@forento/shared/utilities/lexical"

import { backgroundColor, lightTextColor } from "~/themes/school/styles"

type InputFieldProps = {
	label?: string
	value: string
	onChange?: (value: string) => void
	onEnter?: () => void
	placeholder?: string
	inputType?: "text" | "number" | "email" | "password"
	autoComplete?: "given-name" | "family-name" | "email" | "current-password" | "new-password"
	disabled?: boolean
	className?: string
}
const InputField: FC<InputFieldProps> = ({
	label,
	value,
	onChange,
	onEnter,
	placeholder,
	inputType = "text",
	autoComplete,
	disabled,
	className,
}) => {
	const inputId = useId()

	return (
		<TextFieldContainer className={className}>
			<Input
				id={inputId}
				type={inputType}
				value={value}
				onChange={e => onChange?.(e.target.value)}
				onKeyDown={e => e.key === "Enter" && onEnter?.()}
				// We need to always send a placeholder for :placeholder-shown to work
				placeholder={placeholder ?? ""}
				autoComplete={autoComplete}
				disabled={disabled}
				data-sentry-mask={inputType === "password" ? true : undefined}
			/>
			{label !== undefined && <Label htmlFor={inputId}>{label}</Label>}
		</TextFieldContainer>
	)
}

type InputTextAreaProps = {
	value: string
	onChange(value: string): void
	placeholder?: string
	className?: string
}
export const InputTextArea: FC<InputTextAreaProps> = ({ value, onChange, placeholder, className }) => (
	<TextArea
		value={value}
		onChange={e => onChange(e.target.value)}
		// We need to always send a placeholder for :placeholder-shown to work
		placeholder={placeholder ?? ""}
		className={className}
	/>
)

type DynamicInputTextAreaProps = {
	value: string
	onChange(value: string): void
	placeholder?: string
	singleRowHeight?: number
	className?: string
}
export const DynamicInputTextArea: FC<DynamicInputTextAreaProps> = ({
	value,
	onChange,
	placeholder,
	singleRowHeight,
	className,
}) => (
	<DynamicTextArea
		value={value}
		onChange={e => onChange(e.target.value)}
		// We need to always send a placeholder for :placeholder-shown to work
		placeholder={placeholder ?? ""}
		$singleRowHeight={singleRowHeight}
		className={className}
	/>
)

export function useRichTextArea({ label }: { label?: string }) {
	const id = useId()
	const [editorRef, setEditorRef] = useState<LexicalEditorType>()
	const [isEmpty, setEmpty] = useState(true)

	const value = useMemo(
		() => ({
			element: (
				<TextAreaContainer onClick={() => editorRef?.focus()}>
					<RichTextArea id={id} $hideLabel={!isEmpty}>
						<LexicalEditor editorRef={setEditorRef} onEmptyChange={setEmpty} />
					</RichTextArea>
					{label !== undefined && <Label htmlFor={id}>{label}</Label>}
				</TextAreaContainer>
			),
			set(data: string | null): void {
				if (!editorRef) return
				loadEditorState(editorRef, data)
			},
			exportEditorState(): string | null {
				if (!editorRef) return null
				return exportEditorState(editorRef.getEditorState())
			},
		}),
		[editorRef, id, isEmpty, label],
	)

	return value
}

const labelMargin = 4
const hiddenLabelFontSize = 12

const Label = styled.label`
	position: absolute;
	top: ${36 / 2}px;
	left: 24px;
	font-size: 14px;
	line-height: 1;
	user-select: none;
	transform: translateY(-50%);
	transition:
		transform 0.15s ease-out,
		font-size 0.15s ease-out;
`

const TextFieldContainer = styled.div`
	height: 36px;
	flex: 1 0 auto;
	min-width: 0;
	position: relative;

	&:has(${Label}) {
		margin-top: 18px;
	}
`

const TextAreaContainer = styled.div`
	flex: 1 0 auto;
	min-width: 0;
	position: relative;

	&:has(${Label}) {
		margin-top: 18px;
	}
`

const inputStyles = css`
	width: 100%;
	height: 100%;
	font-size: 14px;
	background-color: white;
	border-radius: 10px;
	color: ${lightTextColor};
	border: 1px solid ${backgroundColor};
	padding: 0 24px;

	&::placeholder {
		color: #999;
	}

	&:disabled {
		background-color: #f0f0ef;
	}
`

const hideLabelCss = css`
	& + ${Label} {
		font-size: ${hiddenLabelFontSize}px;
		font-weight: 500;
		transform: translate(-24px, calc(-100% - 20px - ${labelMargin}px));
	}
`

const Input = styled.input`
	${inputStyles}
	line-height: 1;

	&:focus,
	&:not(:placeholder-shown),
	&:-webkit-autofill {
		${hideLabelCss}
	}
`

export const InputLabel = styled.label`
	display: block;
	font-size: ${hiddenLabelFontSize}px;
	font-weight: 500;
	margin-bottom: ${labelMargin}px;
`

const TextArea = styled.textarea`
	${inputStyles}
	min-height: 104px; // Twice the height of an input field
	padding-top: ${(36 - 14 * 1.5) / 2}px;
	padding-bottom: ${(36 - 14 * 1.5) / 2}px;
	line-height: 1.5;
`

const DynamicTextArea = styled(TextAreaAutoSize)<{ $singleRowHeight?: number }>`
	${inputStyles}
	line-height: 1.5;
	resize: none;

	${props =>
		props.$singleRowHeight
			? css`
					padding-top: ${(props.$singleRowHeight - 14 * 1.5 - 2) / 2}px;
					padding-bottom: ${(props.$singleRowHeight - 14 * 1.5 - 2) / 2}px;
				`
			: css`
					padding-top: 16px;
					padding-bottom: 16px;
				`}
`

const RichTextArea = styled.div<{ $hideLabel: boolean }>`
	${inputStyles}
	min-height: 104px; // Twice the height of an input field
	display: flex;
	flex-direction: column;
	padding-top: ${(36 - 14) / 2}px;
	padding-bottom: ${(36 - 14) / 2}px;
	cursor: text;

	&:focus-within {
		${hideLabelCss}
		outline: auto;
	}

	${props => props.$hideLabel && hideLabelCss}
`

export default InputField
